import { IMytaverseFile } from '@interfaces/file';
export interface IMapFloor {
  id: string;
  name: string;
  image: IMytaverseFile;
}

export interface ITeleportLocation {
  id: string;
}

export interface ITeleportLocationPosition {
  x: number;
  y: number;
  z: number;
}

export interface ITeleportLocationRotation {
  yaw?: number;
  pitch?: number;
  roll?: number;
}

// Photo of how a spot or teleport location looks in world, published by the UE
// editor script or uploaded by hand in the CMS. A locked image is never
// overwritten by the automatic capture.
export interface IMapSpotLocationPreview {
  locationPreview?: IMytaverseFile | null;
  isLocationPreviewLocked?: boolean;
}

export interface ITeleportPreview {
  teleportId: string;
  previewImage: IMytaverseFile | null;
  isPreviewLocked?: boolean;
  id?: string;
  teleportLocation?: ITeleportLocation;
  name?: string;
  description?: string | null;
  screenshotName?: string;
  positionInWidget?: number;
  showInUi?: boolean;
  position?: ITeleportLocationPosition;
  rotation?: ITeleportLocationRotation;
}

export type TrainingTaskType = 'procedural' | 'evaluative';

export interface ITrainingTask {
  taskId: string;
  name: string;
  expectedDurationSec: number;
  // When absent, the task is treated as 'procedural' (see trainingAnalytics).
  type?: TrainingTaskType;
}

export interface ITrainingModule {
  moduleId: string;
  name: string;
  tasks: ITrainingTask[];
}

export interface IMapLayoutElementGeometry {
  kind: 'level' | 'class' | 'greybox';
  asset?: string;
  package?: string;
  available: boolean;
  cooked?: boolean;
}

// The brandable spots a build found inside the element's room level. Computed at build
// time, so it is absent whenever the level could not be read — which is not the same
// answer as `total: 0`, and one shipped corridor really does have no spots at all.
// One place for a picture, with the size the build measured for it.
//
// Read for drawing only, and for one specific thing the counts above cannot answer: how wide the
// frame is against how tall. The shipped kit holds eleven distinct shapes — from an exact square
// (912 × 912) to an 11:1 banner (3516 × 314) — and until this field existed every one of them was
// drawn at the same guessed ratio, so a screen gallery and a picture gallery looked alike in the
// picker.
export interface IMapLayoutSpotSurface {
  id: string;
  type?: string;
  // Absent rather than zero for a surface the build could not measure. Zero would draw as a
  // hairline, which is not what "unmeasured" looks like.
  widthCm?: number;
  heightCm?: number;
}

export interface IMapLayoutElementSpots {
  total: number;
  // Authored spot types (IMAGE / VIDEO), already normalised the way the runtime does.
  byType?: Record<string, number>;
  // Unique within a module, not within a world: a module placed twice brings its ids twice.
  ids?: string[];
  // Present only when the module's own ids are already broken — an authoring bug, where
  // the second spot silently shows the first one's media.
  duplicateIds?: string[];
  unnamed?: number;
  // Added beside `total` / `byType` / `ids` rather than replacing any of them: those three answer
  // "how many and of what kind", which is what the row counts and the picker's prose need, and this
  // answers "what shape", which only the drawing needs. Absent for a build older than the field.
  surfaces?: IMapLayoutSpotSurface[];
}

export interface IMapLayoutElement {
  id: string;
  displayName: string;
  category: string;
  // Authored prose about what this element is, shown where the picker has room for a sentence —
  // the wall-layout step. Optional and purely descriptive: the build copies authored catalog
  // fields verbatim, so supplying it needs no UE or backend change, and its absence falls back to
  // the spot counts the build measured.
  description?: string;
  thumbnailUrl?: string;
  // The full-size shot of the same capture the tile is a downscale of. Relative to the catalog file
  // exactly as `thumbnailUrl` is, and just as unfetchable on its own — `useMapEditorCatalog`
  // replaces both with what the build published out of its package.
  previewUrl?: string;
  // Superseded by the family/direction pair below. Still read for catalogs shipped by a
  // build that predates the split — and its left/right were inverted, so it is mapped by
  // the turn it produced rather than by the word in it (see mapEditorCatalog).
  type?: string;
  // Kept plain strings rather than unions: a newer build may ship a family or direction
  // this hub version has never heard of, and the picker groups on whatever it finds
  // rather than dropping what it cannot name.
  family?: string;
  direction?: string;
  size?: string;
  // 'module' (default), 'end' — a cap that seals the chain — or 'start', the lobby the chain
  // opens in. UE drops every room requested after a cap and refuses a start row anywhere but
  // first, so neither is ever offered as a pickable module.
  role?: string;
  geometryLevel?: string;
  geometryClass?: string;
  overrideExit?: boolean;
  exitOffset?: number[];
  exitYawDeg?: number;
  geometry?: IMapLayoutElementGeometry;
  spots?: IMapLayoutElementSpots;
  // Semantic surfaces this element's rooms can be re-finished on: `wall`, `floor`, `ceiling`,
  // `trim`, always lower case.
  //
  // A build-time projection of the level's own truth — the surfaces are authored on a finish-map
  // actor inside each module's level, and the same pass that seeds those actors writes the result
  // back here. That is what lets the hub offer surfaces for a room sitting in an unpublished draft:
  // a preview spawn is never answered with CURRENT_GEOMETRY_LIST, so the report cannot answer for a
  // room nobody has published yet.
  //
  // A surface nobody has classified contributes nothing, so a short list means the module is
  // part-way through that pass rather than that it has few surfaces. Absent means a build older
  // than the field.
  finishSlots?: string[];
  // The element's plan footprint in centimetres, in its own frame, as `[minX, minY, maxX, maxY]` —
  // the same four numbers the overlap test uses. Measured by the build from the room level, so it
  // is absent for an element whose level could not be read, and for a build older than the field.
  //
  // Read for drawing only. The picker's plan glyphs are the one place in this hub that has to know
  // how long a module is and which way it sweeps, and there is nothing else in the catalog that
  // says: two corridors differing only in length are otherwise identical rows of text.
  footprint?: number[];
}

// A runtime finish is a material put on a semantic surface of a room while the world is running.
// UE owns the master material and the cooked textures; the hub only picks a preset and moves its
// parameters, which travel as plain numbers over the existing layout messages. The registry below
// is authored alongside the room kit and ships inside the same catalog — see
// UeProject/Plugins/Mytaverse/MytaMapEditor/Docs/runtime-materials-plan.md.

// One cooked static-switch permutation of the master. The hub never sets a static switch itself —
// a permutation cannot change at runtime — so it picks the variant whose permutation a finish needs.
export interface IMapFinishVariant {
  id: string;
  displayName: string;
  staticSwitches?: Record<string, boolean>;
  // Texture roles a set has to supply for this variant to render correctly: `roughness-map` reads a
  // roughness map, so a set without one pairs with it wrongly. Absent means it constrains nothing.
  requiredRoles?: string[];
}

export interface IMapFinishGroup {
  id: string;
  displayName: string;
}

// `key` is what the hub renders and sends; `param` is the master's real parameter name, which is
// ugly on purpose (`Height Ratio`, `Min Steps`, one typo) and stays UE-side. Keeping them apart is
// what stops a rename inside a material from becoming a lock-step hub + UE + backend deploy.
export interface IMapFinishScalar {
  key: string;
  param: string;
  group: string;
  displayName: string;
  default: number;
  min: number;
  max: number;
  step: number;
  // Costs frame time as it rises — parallax step counts. The UI warns; the value is clamped rather
  // than refused, since refusing a whole layout over one slider is hostile.
  perfCritical?: boolean;
  // Suffix shown after the number. Deliberately separate from displayName and deliberately not
  // localized: the symbols are universal, and displayName is the half that gets translated. Absent
  // means no suffix — three of the shipped scalars are unitless 0..1 ratios — rather than missing.
  unit?: string;
  // Key of another scalar this one may not go above. `reliefStepsMin` names `reliefStepsMax`: a
  // near-field step count above the far-field one is not a state the material has an answer for.
  //
  // A declared relationship rather than a derived one, and general rather than a special case for
  // that pair. The authority enforces it over the whole resolved set and lowers the constrained
  // value — never raises the ceiling, since the ceiling is what the author last moved on purpose —
  // so the UI has to hold the same line: a slider that could cross it would be silently pulled back
  // and would then disagree with the world it is describing.
  mustNotExceed?: string;
  // What this parameter does, in one line, as the kit describes it. Authored in the schema so a kit
  // that adds a parameter also explains it, without a web deploy — the same reason the rest of the
  // menu is generated. English; the hub overlays a translation where it knows the key. Absent means
  // the kit has not described this one, and the panel then shows the label alone rather than an
  // empty tooltip.
  description?: string;
}

export interface IMapFinishVector {
  key: string;
  param: string;
  group: string;
  displayName: string;
  // RGBA, each channel 0..1.
  default: number[];
  // What this parameter does, in one line, as the kit describes it. Authored in the schema so a kit
  // that adds a parameter also explains it, without a web deploy — the same reason the rest of the
  // menu is generated. English; the hub overlays a translation where it knows the key. Absent means
  // the kit has not described this one, and the panel then shows the label alone rather than an
  // empty tooltip.
  description?: string;
}

export interface IMapFinishTexture {
  key: string;
  param: string;
  // Which map of a texture set fills this parameter: albedo | normal | height | roughness.
  role: string;
}

export interface IMapFinishSchema {
  master: string;
  variants: IMapFinishVariant[];
  groups: IMapFinishGroup[];
  scalars: IMapFinishScalar[];
  vectors: IMapFinishVector[];
  // The role-to-parameter mapping. Carried for completeness only: the hub chooses whole texture
  // sets and never addresses a single map, so nothing in the menu renders from this.
  textures: IMapFinishTexture[];
}

// The maps of one surface, addressed as a unit because they are meaningless apart — a menu that
// offered four independent texture pickers would be offering the author a way to build nonsense.
export interface IMapTextureSet {
  id: string;
  displayName: string;
  albedo?: string;
  normal?: string;
  height?: string;
  roughness?: string;
}

// A named starting point, not the only way to describe a finish. `scalars`/`vectors` hold only what
// differs from the schema default, so a later change to the master keeps reaching every finish that
// does not override it.
export interface IMapFinish {
  id: string;
  displayName: string;
  thumbnailUrl?: string;
  variant: string;
  textureSet: string;
  scalars?: Record<string, number>;
  vectors?: Record<string, number[]>;
}

// A finish as applied to one semantic slot.
//
// Values are fully resolved rather than a diff from the preset. The trade is deliberate: a resolved
// entry does not inherit a later change to the master's defaults, and that is the point — what the
// author saw and published is what everyone keeps seeing. `finish` rides along as provenance only:
// it is what "reset to finish" resets to and what names the entry in the panel.
export interface IMapLayoutFinish {
  slot: string;
  finish?: string;
  variant: string;
  textureSet: string;
  scalars: Record<string, number>;
  vectors: Record<string, number[]>;
}

export interface IMapLayoutCatalog {
  version: number;
  elements: IMapLayoutElement[];
  // Element id UE closes an open chain with. Emitted only when it names an element the
  // build actually ships; absent means this build closes no chains.
  defaultEndElement?: string;
  // The lobby this kit considers its default — the room a visitor arrives in, at the head of the
  // chain. Named for the hub to pre-select, not for UE to substitute: UE ships
  // `bAutoOpenChainStart` off, so a layout that names no lobby opens on the entrance the host map
  // bakes in rather than on this one. Absent means the kit offers no lobby to switch to.
  defaultStartElement?: string;
  // The runtime-finish registry. All four are optional and belong together: a build that ships none
  // of them simply has no finish menu, and every reader tolerates each one missing on its own.
  finishSchema?: IMapFinishSchema;
  textureSets?: IMapTextureSet[];
  finishes?: IMapFinish[];
}

// One placement in a saved layout: which catalog element, and which placement of it this is.
//
// The key is what makes a spot addressable per placement. Place the same module twice and both
// copies carry the same authored spot ids, so content assigned to one appeared in both; UE builds
// a per-placement spot id out of this key, and treats it as opaque — see the stable-spot-ids
// contract in UeProject/Plugins/Mytaverse/MytaMapEditor/Docs/stable-spot-ids-web-task.md.
export interface IMapLayoutRoomEntry {
  id: string;
  key: string;
}

export interface IMapLayout {
  version: number;
  // v1 records hold bare element ids and stay readable forever — a layout nobody edits does not
  // need rewriting, and it is migrated to entries the next time its author saves.
  //
  // Typed as one mixed array rather than `string[] | IMapLayoutRoomEntry[]`: the reader is
  // defensive per entry anyway (this crosses a DB record written by an older hub), and a union of
  // arrays forces a cast at every read for no extra safety.
  rooms: (string | IMapLayoutRoomEntry)[];
  // The lobby this layout opens in — a catalog element id, published as `rooms[0]`.
  //
  // Kept out of `rooms` rather than stored as its first entry, because it is not a link in the
  // chain: the author does not move, reorder or delete it, the picker never offers it, and every
  // reader of `rooms` — the room list, the refusal markers, the finish overrides — counts positions
  // that would all shift by one if it lived there. It is the chain's opening, held beside the chain.
  //
  // Absent means this layout names no lobby, which is what every record written before the feature
  // says. That is not a gap to fill in: it opens on the entrance the host map bakes in, which is
  // what those layouts have always done.
  startElement?: string;
  // Finishes every room takes unless it overrides the same slot itself.
  //
  // The default is the primary form rather than an optimisation. Authors finish by surface, not by
  // room — "concrete on every wall" is one decision — and writing that per room instead costs 461
  // bytes a slot: a thirty-room chain with four finished slots comes to 62 KiB against UE's 64 KiB
  // MaxLayoutJsonLength, which is not a refusal but a closed connection. As a default it is 10 KiB.
  finishDefaults?: IMapLayoutFinish[];
  // What individual placements override, keyed by placement key.
  //
  // Kept beside the rooms rather than inside their entries so reading it does not depend on the room
  // parse: that parse re-mints a key it finds twice, and an override addressed to the original would
  // then land on whichever of the two kept it. Keyed separately, an override whose room did not
  // survive the read simply has no room, which is the right answer for a placement whose identity
  // could not be trusted.
  roomFinishes?: Record<string, IMapLayoutFinish[]>;
}

// 3 since runtime finishes. v2 is per-placement keys (MV-1585) and v1 is `rooms: string[]`; both
// stay readable, because every field this version adds is optional and read defensively.
export const MAP_LAYOUT_VERSION = 3;

export interface IMapRegion {
  name: string;
  type?: string;
  spots?: string[];
  // Marks the region UE designates as the map's entry point. When present,
  // this real region is used as the "on join" AI trigger instead of the
  // synthetic 'landing' pseudo-trigger.
  isStartingPoint?: boolean;
}

export interface IMapFeatureFlag {
  slug: string;
  name?: string;
  // Absent means on, so a flag added to a map applies until an experience opts
  // out; a flag that ships off must be enabled per experience.
  enabledByDefault?: boolean;
}

export interface IBrandingProperty {
  propertyId: string;
  displayName: string;
  // Kept a plain string rather than a union: a newer build may ship a type this
  // hub version has no widget for, and it must survive the round trip.
  type: string;
  // Absent means "no default" — an empty string is a default of empty.
  default?: string;
  authoredAssetPath?: string;
  // Absent means an open field; present means a closed set.
  allowedValues?: string[];
}

// The catalogue entry: what a build declares as brandable. UE calls this
// `brandingId` in the build metadata — renamed here because `brandingId` is the
// id of an IBranding record.
export interface IBrandingEntity {
  entityId: string;
  displayName: string;
  entityType: string;
  maps: string[];
  properties: IBrandingProperty[];
}

// The schema of a brandable *class*, read off its class default rather than off
// a placed actor. It is what an editor is rendered from for an object that does
// not exist until the session is running, so it carries no `maps` — a class is
// not in any level until something spawns it.
//
// `properties` is byte for byte the same shape an entity's is, which is what
// lets one renderer serve both sources. The only difference is what `default`
// means: on an entity it is what that placed actor was authored with, on a type
// it is what the class itself ships.
export interface IBrandingType {
  entityType: string;
  displayName: string;
  properties: IBrandingProperty[];
}

// One brandable object standing in the world right now, as UE reports it over
// BRANDING_ENTITIES. Not a catalogue entry: it names the object and the class to
// look its schema up under, and nothing else.
export interface ILiveBrandingEntity {
  // The only key. Opaque — UE composes it per placement, and it may carry a `#`.
  // Never parse, split or re-derive it.
  brandingId: string;
  // Join key into the build's `brandingTypes`.
  entityType: string;
  // For the UI only; never a key.
  displayName: string;
  // Diagnostics only. Present when a placement stamped its own id over the one
  // the asset authored, so its *absence* means the object is on its authored id
  // — in practice, that it was placed in the map rather than spawned.
  authoredId?: string;
}

export interface IMap {
  mapId: string;
  ueIdentifier: string;
  name: string;

  displayName: string;
  mapName: string;
  imageBaseMap: string;
  id: string;
  thumbnail: string;

  floorPlans?: IMapFloor[];
  teleportPreviews?: ITeleportPreview[];
  trainingModules?: ITrainingModule[];
  regions?: IMapRegion[];
  // Legacy, and dead on both sides: the upload writes only the build's own
  // `clientParams.layoutCatalogs`, and nothing has read this since MV-2004. Kept because records
  // persisted before the kit moved onto the build still carry it.
  layoutCatalog?: IMapLayoutCatalog;
  mapFeatureFlags?: IMapFeatureFlag[];
  brandingEntities?: IBrandingEntity[];
}
